/**
 * QnaThreadCard — one submission's Rückfragen card (P2 Placement Slice 9, Tab 3c). Header = carrier +
 * Nummer + the Slice-8 SubmissionStatusBadge (read-only) + the Q&A status badge. Body = the thread
 * (frist line + editor, «offen seit», the message list, the composer, the status actions), or the
 * empty hint + composer when there is no thread yet. Opening a card with unread questions marks them
 * read once (tenant-wide, Q77).
 */

import { useEffect, useRef, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { CarrierLogo } from './CarrierLogo';
import { SubmissionStatusBadge } from './SubmissionStatusBadge';
import { QnaThreadStatusBadge } from './QnaThreadStatusBadge';
import { QnaMessageList } from './QnaMessageList';
import { QnaAntwortComposer } from './QnaAntwortComposer';
import { SubmissionGrundDialog } from './SubmissionGrundDialog';
import { QNA_KEINE_RUECKFRAGEN_HINWEIS, QNA_NICHT_VERSANDT_HINWEIS } from '@brokly/shared';
import type { QnaSubmissionEintrag } from '@brokly/shared';
import { dateShortDe, dateTimeShortDe, daysUntil } from '../../lib/format';

export function QnaThreadCard({
  item,
  canWrite,
  canSend,
  aktionErlaubt,
  mailboxVerbunden,
  sending,
  error,
  onAntworten,
  onErledigt,
  onOffen,
  onFrist,
  onGelesen,
}: {
  item: QnaSubmissionEintrag;
  canWrite: boolean;
  canSend: boolean;
  aktionErlaubt: boolean;
  mailboxVerbunden: boolean;
  sending?: boolean;
  error?: string | null;
  onAntworten: (text: string, opts: { empfaenger_bestaetigt_an?: string }) => void;
  onErledigt: (grund: string) => void;
  onOffen: () => void;
  onFrist: (date: string) => void;
  onGelesen: () => void;
}) {
  const t = item.thread;
  const [editingFrist, setEditingFrist] = useState(false);
  const [fristInput, setFristInput] = useState(t?.antwort_frist_date ?? '');
  const [grundOpen, setGrundOpen] = useState(false);
  const markedRef = useRef(false);

  useEffect(() => {
    if (t && t.ungelesen > 0 && canWrite && !markedRef.current) {
      markedRef.current = true;
      onGelesen();
    }
  }, [t, canWrite, onGelesen]);

  const sendLabel = t && t.status === 'offen' ? 'Antwort senden' : 'Nachricht senden';
  const openMail = (communicationId: string) => window.open(`/posteingang/${communicationId}`, '_blank', 'noopener');

  return (
    <section className="rounded-lg border border-border p-4" data-testid={`qna-card-${item.carrier_uid}`}>
      <header className="mb-3 flex flex-wrap items-center gap-2">
        <CarrierLogo name={item.name} logoUrl={item.logo_url} size="sm" />
        <span className="font-medium">{item.name ?? item.carrier_uid}</span>
        <span className="mono-num text-xs text-muted-foreground">{item.submission_number}</span>
        <SubmissionStatusBadge status={item.submission_status} fristAbgelaufen={false} />
        {t && <QnaThreadStatusBadge status={t.status} ueberfaellig={t.ueberfaellig} ungelesen={t.ungelesen} />}
      </header>

      {t === null ? (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            {item.antwort_moeglich ? QNA_KEINE_RUECKFRAGEN_HINWEIS : QNA_NICHT_VERSANDT_HINWEIS}
          </p>
          {canSend && item.antwort_moeglich && aktionErlaubt && (
            // Same key in both branches: the typed text survives a reload that creates the thread.
            <QnaAntwortComposer
              key="composer"
              onSend={onAntworten}
              sending={sending}
              mailboxVerbunden={mailboxVerbunden}
              error={error}
              sendLabel="Nachricht senden"
              empfaenger={item.carrier_email_to}
              empfaengerAbweichend={false}
              carrierDomain={item.carrier_domain}
            />
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {t.status === 'offen' && t.antwort_frist_date && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span>Antwort erwartet bis {dateShortDe(t.antwort_frist_date)}</span>
              {(() => {
                const d = daysUntil(t.antwort_frist_date);
                return d !== null ? (
                  <span className="text-xs text-muted-foreground">
                    ({d < 0 ? `${-d} Tage überfällig` : d === 0 ? 'heute' : `in ${d} Tagen`})
                  </span>
                ) : null;
              })()}
              {canWrite && !editingFrist && (
                <button
                  type="button"
                  className="text-xs text-primary underline"
                  onClick={() => {
                    setFristInput(t.antwort_frist_date ?? '');
                    setEditingFrist(true);
                  }}
                  data-testid="qna-frist-edit"
                >
                  bearbeiten
                </button>
              )}
              {canWrite && editingFrist && (
                <span className="inline-flex items-center gap-1">
                  <input
                    type="date"
                    className="rounded-md border border-border bg-background px-1 text-xs"
                    value={fristInput}
                    onChange={(e) => setFristInput(e.target.value)}
                    data-testid="qna-frist-input"
                  />
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setEditingFrist(false);
                      if (fristInput) onFrist(fristInput);
                    }}
                    data-testid="qna-frist-save"
                  >
                    Speichern
                  </Button>
                </span>
              )}
            </div>
          )}
          {t.offen_seit && <p className="text-xs text-muted-foreground">offen seit {dateTimeShortDe(t.offen_seit)}</p>}

          <QnaMessageList messages={t.messages} onOpenMail={openMail} />

          {t.versand_laeuft && <p className="text-xs text-muted-foreground">Versand läuft …</p>}
          {t.letzter_versandfehler && (
            <p className="text-xs text-status-danger" data-testid="qna-card-versandfehler">
              {t.letzter_versandfehler === 'record_failed'
                ? 'Antwort wurde versendet, aber nicht erfasst - bitte nicht erneut senden.'
                : `Letzter Versand fehlgeschlagen: ${t.letzter_versandfehler}`}
            </p>
          )}

          {canSend && aktionErlaubt && (
            <QnaAntwortComposer
              key="composer"
              onSend={onAntworten}
              sending={sending}
              mailboxVerbunden={mailboxVerbunden}
              error={error}
              sendLabel={sendLabel}
              empfaenger={t?.antwort_empfaenger ?? item.carrier_email_to}
              empfaengerAbweichend={t?.antwort_empfaenger_abweichend ?? false}
              carrierDomain={item.carrier_domain}
            />
          )}

          {canWrite && aktionErlaubt && (
            <div className="flex gap-2">
              {t.status === 'offen' && (
                <Button variant="ghost" onClick={() => setGrundOpen(true)} data-testid="qna-erledigt">
                  Als erledigt markieren
                </Button>
              )}
              {t.status === 'erledigt' && (
                <Button variant="ghost" onClick={onOffen} data-testid="qna-offen">
                  Erneut öffnen
                </Button>
              )}
            </div>
          )}

          <SubmissionGrundDialog
            open={grundOpen}
            onClose={() => setGrundOpen(false)}
            title="Rückfrage als erledigt markieren"
            grundRequired={false}
            submitLabel="Erledigen"
            pending={false}
            onSubmit={(grund) => {
              setGrundOpen(false);
              onErledigt(grund);
            }}
          />
        </div>
      )}
    </section>
  );
}
